import { MessageSquare, BookOpenCheck, PenTool } from "lucide-react";

const workshops = [
  {
    no: 1,
    strand: "Oral Language",
    title: "The ELL Learner: Language, Culture & the Classroom",
    focus: "SLA theory, BICS/CALP, affective filter, cultural identity",
    components: "C1, C2",
  },
  {
    no: 2,
    strand: "Oral Language",
    title: "Talk That Teaches: Building Oral Academic Language",
    focus: "Academic talk, questioning, sentence frames, translanguaging",
    components: "C2, C3, C4",
  },
  {
    no: 3,
    strand: "Reading",
    title: "Unlocking Texts: Reading with ELL Learners",
    focus: "Simple View of Reading, vocabulary, phonics, pre-reading",
    components: "C1, C2, C3",
  },
  {
    no: 4,
    strand: "Reading",
    title: "Reading Deeper: Comprehension & Critical Thinking",
    focus: "Comprehension strategies, culturally relevant texts, inference",
    components: "C2, C3, C4",
  },
  {
    no: 5,
    strand: "Writing",
    title: "Writing as Thinking: Supporting ELLs to Write",
    focus: "Writing process, genre, pre-writing scaffolds, mentor texts",
    components: "C2, C3",
  },
  {
    no: 6,
    strand: "Writing",
    title: "Words on the Page: Vocabulary, Mechanics & Voice",
    focus: "Vocabulary in writing, feedback, student voice, portfolio",
    components: "C3, C4",
  },
];

const strandIcon = {
  "Oral Language": <MessageSquare size={16} />,
  Reading: <BookOpenCheck size={16} />,
  Writing: <PenTool size={16} />,
};

export default function Workshops() {
  return (
    <section id="workshops" className="section bg-[var(--sage)]">
      <div className="container-pl">
        <div className="max-w-2xl">
          <div className="overline">The workshop programme</div>
          <h2 className="font-display font-bold text-3xl md:text-5xl mt-3 leading-tight">
            Six workshops. One coherent learning journey.
          </h2>
          <p className="mt-5 text-[var(--slate)] text-base md:text-lg">
            Two workshops per strand, designed to move from foundational theory to practical
            classroom application — with structured portfolio reflection in every session.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="workshops-grid">
          {workshops.map((w) => (
            <article
              key={w.no}
              className="card flex flex-col h-full"
              data-testid={`workshop-${w.no}`}
            >
              <div className="flex items-center justify-between">
                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[var(--forest)] text-white text-xs font-display font-semibold">
                  {strandIcon[w.strand]} {w.strand}
                </div>
                <div className="font-display font-bold text-2xl text-[var(--forest)]">
                  W{w.no}
                </div>
              </div>
              <h3 className="font-display font-semibold text-lg mt-4 leading-snug text-[var(--charcoal)]">
                {w.title}
              </h3>
              <p className="mt-3 text-sm text-[var(--slate)] leading-relaxed flex-1">{w.focus}</p>
              <div className="mt-5 pt-4 border-t border-[var(--border)] flex items-center justify-between text-xs text-[var(--slate)]">
                <span>2 hours</span>
                <span className="font-mono">{w.components}</span>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-12 card max-w-4xl" data-testid="assessment-card">
          <div className="overline">Assessment</div>
          <h3 className="font-display font-semibold text-xl mt-3">
            Professional Portfolio · assessed across four NZQA components
          </h3>
          <div className="mt-5 grid sm:grid-cols-3 gap-4 text-sm">
            <Item
              number="6"
              label="Workshop reflections"
              detail="One in-session portfolio entry per workshop"
            />
            <Item
              number="3"
              label="Classroom practice tasks"
              detail="Applied evidence from real ELL students"
            />
            <Item
              number="1"
              label="Final synthesis"
              detail="Reflection on growth across all four components"
            />
          </div>
          <p className="mt-5 text-xs text-[var(--slate)] leading-relaxed">
            To gain the micro-credential, all four portfolio domains must reach{" "}
            <strong className="text-[var(--charcoal)]">Achieved (3)</strong> or above. Awarded with
            Distinction for the majority at Excellence (4).
          </p>
        </div>
      </div>
    </section>
  );
}

function Item({ number, label, detail }) {
  return (
    <div className="p-4 rounded-lg bg-[var(--sage)] border border-[var(--border)]">
      <div className="font-display font-bold text-3xl text-[var(--forest)]">{number}</div>
      <div className="font-display font-semibold text-sm mt-1 text-[var(--charcoal)]">
        {label}
      </div>
      <div className="text-xs text-[var(--slate)] mt-1 leading-snug">{detail}</div>
    </div>
  );
}
